import { Context } from "%/dynamic-component-editor/classes/context";
import App from "@bloglovin/inspire-engine/App";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { DEFAULT_USER_AVATAR } from "%/constants/user-constants";
import { stripClientOnlyFieldDefKeys } from "%/dynamic-component-editor/helpers/engine-field-def-keys";

// Studio-side wiring for the legacy CONTEXT entities (Story / SubscriptionPlan
// / Show / Season / Episode / Event, + the hidden Publication scope). The defs
// are SERVER-AUTHORITATIVE (window.blclient.data.legacyEntities); this bootstrap
// only adapts them for the DCE — stamps per-field canvas example data and
// registers the "Legacy" Context source. Their field elements light up the
// palette through the SAME generic InspireEntity path app entities use.
//
// DISTINCT from studio-legacy-entities-source.js (the OLD display-name-only
// source for the seeded standard pages' Legacy* rows) — that stays untouched.

export const LEGACY_ENTITY_SOURCE_KEY = "Legacy";

// Publication is hidden from pickers; this phase it exists only as a oneEntity
// target (Story/Plan/Event.publication). Expose just its identity fields on
// the off-chance a config surfaces them — feeds are Phase 2.
const HIDDEN_ENTITY_PALETTE_FIELDS = ["id", "name", "logo"];

function injectedLegacyEntityDefs() {
  return window.blclient?.data?.legacyEntities ?? {};
}

// The built-in User standard entity's def (name / email / avatar), surfaced so
// `currentUser` lights up its field elements like every other context. Server-
// authoritative (window.blclient.data.standardUserEntity), delivered alongside
// the legacy defs; empty object when absent.
function injectedStandardUserEntityDef() {
  const def = window.blclient?.data?.standardUserEntity;
  return def && def.entityType ? def : null;
}

// Canvas preview values, derived per field — the server defs carry no
// exampleData (the engine's field-def validator owns their shape); the palette
// copies get representative previews here so build mode renders content
// without a bound instance.
function exampleDataForField(fieldName, fieldDef) {
  const label = fieldName
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, first => first.toUpperCase());
  switch (fieldDef.type) {
    case "int":
      return 12;
    case "float":
      return 9.99;
    case "boolean":
      return false;
    case "datetime":
      // A fixed, obviously-formatted moment beats "0" (1970) on canvas.
      return 1750000000;
    case "stringArray":
      // Renders through the text fallback this phase (tag-list chips are
      // Phase 2) — a comma string reads cleaner than a raw array.
      return "tag one, tag two";
    case "email":
      return "person@example.com";
    case "image":
      return fieldName === "avatar" ? DEFAULT_USER_AVATAR : "";
    case "video":
    case "story":
      return "";
    default:
      return `Example ${label}`;
  }
}

// The User def in the same DCE-shaped form as the legacy defs (per-field
// exampleData, isLegacyEntity for the bare picker label + flat-map merge).
// Returned as a single { User: config } entry so it registers alongside them.
function getStandardUserContextsEntities() {
  const definition = injectedStandardUserEntityDef();
  if (!definition) {
    return {};
  }
  const fields = {};
  for (const [fieldName, fieldDef] of Object.entries(definition.fields ?? {})) {
    // Drop the User's non-field-element extras (appUser back-link, permissions
    // feed) so the inspector shows only its renderable profile fields.
    if (fieldDef.type === "oneEntity" || fieldDef.type === "manyEntities") {
      continue;
    }
    fields[fieldName] = {
      ...fieldDef,
      exampleData: exampleDataForField(fieldName, fieldDef),
    };
  }
  // Stamp appName so a binding authored against this entity persists
  // context.appName = "CreatorStudio" (the host app that holds legacy/standard
  // entities). Without it the binding's appName is undefined, and the published
  // render's App.get(undefined) throws + relatedEntity misses → blank element.
  return { [definition.entityType]: { ...definition, isLegacyEntity: true, appName: "CreatorStudio", fields } };
}

// DCE-shaped entity configs: the server def + per-field exampleData, keyed by
// entityType — the exact shape contextsEntities / Context-source entries want.
export function getLegacyContextsEntities() {
  const entities = {};
  for (const [entityType, definition] of Object.entries(injectedLegacyEntityDefs())) {
    const fields = {};
    for (const [fieldName, fieldDef] of Object.entries(definition.fields ?? {})) {
      if (definition.hiddenFromPickers && !HIDDEN_ENTITY_PALETTE_FIELDS.includes(fieldName)) {
        continue;
      }
      fields[fieldName] = {
        ...fieldDef,
        exampleData: exampleDataForField(fieldName, fieldDef),
      };
    }
    entities[entityType] = {
      ...definition,
      // Marks these configs as legacy for the DCE: bare (un-prefixed) picker
      // labels, and the flat map merges them alongside app entities.
      isLegacyEntity: true,
      // appName = the host app that holds legacy entities; a binding authored
      // against this entity persists context.appName so the published render
      // resolves App.get / the Context source instead of App.get(undefined).
      appName: "CreatorStudio",
      fields,
    };
  }
  // The built-in User standard entity rides the same source so `currentUser`
  // lights up its field elements (name / email / avatar) — same generic path.
  return { ...entities, ...getStandardUserContextsEntities() };
}

export function getHostEntityTypes() {
  const standardUserEntityType = injectedStandardUserEntityDef()?.entityType;
  const legacyEntityTypes = Object.keys(injectedLegacyEntityDefs());
  if (!standardUserEntityType) {
    return legacyEntityTypes;
  }
  return [...legacyEntityTypes, standardUserEntityType];
}

// Register the "Legacy" Context source so the editor store's
// allAvailableContextEntities / contextsEntities surface these defs and their
// field elements mint through InspireEntity.getElements(). Idempotent.
export function registerStudioLegacyEntityDefs() {
  const entities = getLegacyContextsEntities();
  if (!Object.keys(entities).length) {
    return;
  }
  Context.addSource(LEGACY_ENTITY_SOURCE_KEY, {
    displayName: "Legacy",
    entities,
  });
}

// Merge the RAW server-delivered legacy entity defs into the CreatorStudio
// engine App's entity registry. The PHP runtime injects these into
// App::$entities server-side (App::addStandardEntities); the JS engine has no
// twin, so without this the engine can't build a FeedStore for a legacy entity
// and every externallyBacked Publication feed (FeedV2 stories/shows/…) silently
// no-ops. Unlike registerStudioLegacyEntityDefs (which adapts defs for the DCE
// Context source / palette), this passes the defs as delivered, minus the
// DCE-only field hints the engine's validator would reject.
// Must run on EVERY boot path that renders a DynamicComponent: the builder
// (main-vue) AND the standalone Production / Staging renders. Idempotent.
export function registerLegacyEntitiesIntoEngine() {
  const defs = injectedLegacyEntityDefs();
  if (!Object.keys(defs).length) {
    return;
  }
  // App.get throws for an un-booted app — the CreatorStudio app may not exist
  // yet on this boot path. Guard so callers can invoke unconditionally; the
  // render paths call this again once the app is booted.
  let studio = null;
  try {
    studio = App.get(contextualAppName());
  } catch {
    return;
  }
  if (!studio || typeof studio.registerEntityDefinitions !== "function") {
    return;
  }
  // PHP serializes an empty associative field (`'actions' => []`) as a JSON
  // array `[]`, but the engine's entity-def validator requires `actions` (and
  // `fields`) to be OBJECTS. Coerce empty-array placeholders to `{}` so the
  // server-delivered legacy defs validate. (Non-empty maps already serialize
  // as objects.)
  // The engine validates a def the first time it instantiates the entity, and a
  // DCE-only hint like `storyType` fails that -- see engine-field-def-keys.js.
  const normalized = {};
  Object.keys(defs).forEach(entityType => {
    const def = { ...defs[entityType] };
    if (Array.isArray(def.actions)) {
      def.actions = {};
    }
    if (Array.isArray(def.fields)) {
      def.fields = {};
    }
    normalized[entityType] = stripClientOnlyFieldDefKeys(def);
  });
  studio.registerEntityDefinitions(normalized);
}
